import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Loader2, FileText } from 'lucide-react';
import { toast } from 'sonner';

const DailyReportDialog = ({ open, onClose, onSubmit }) => {
  const { api } = useAuth();
  const [saving, setSaving] = useState(false);
  const [report, setReport] = useState({
    summary: '',
    callsMade: 0,
    leadsContacted: 0,
    meetingsScheduled: 0,
    remarks: '',
  });

  const handleSubmit = async () => {
    if (!report.summary.trim()) {
      toast.error('Please provide a summary');
      return;
    }

    setSaving(true);
    try {
      await api.post('/daily-reports', report);
      toast.success('Daily report submitted');
      onSubmit();
    } catch (err) {
      toast.error('Failed to submit report');
      setSaving(false);
    }
  };

  const handleSkip = () => {
    onSubmit();
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            End of Day Report
          </DialogTitle>
          <DialogDescription>
            Submit your daily activity summary before logging out
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div>
            <Label htmlFor="summary">Summary *</Label>
            <Textarea
              id="summary"
              value={report.summary}
              onChange={(e) => setReport({ ...report, summary: e.target.value })}
              placeholder="Brief summary of today's activities..."
              data-testid="report-summary"
            />
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div>
              <Label htmlFor="calls">Calls Made</Label>
              <Input
                id="calls"
                type="number"
                min="0"
                value={report.callsMade}
                onChange={(e) => setReport({ ...report, callsMade: parseInt(e.target.value) || 0 })}
                data-testid="report-calls"
              />
            </div>
            <div>
              <Label htmlFor="contacts">Leads Contacted</Label>
              <Input
                id="contacts"
                type="number"
                min="0"
                value={report.leadsContacted}
                onChange={(e) => setReport({ ...report, leadsContacted: parseInt(e.target.value) || 0 })}
                data-testid="report-contacts"
              />
            </div>
            <div>
              <Label htmlFor="meetings">Meetings</Label>
              <Input
                id="meetings"
                type="number"
                min="0"
                value={report.meetingsScheduled}
                onChange={(e) => setReport({ ...report, meetingsScheduled: parseInt(e.target.value) || 0 })}
                data-testid="report-meetings"
              />
            </div>
          </div>

          <div>
            <Label htmlFor="remarks">Additional Remarks</Label>
            <Textarea
              id="remarks"
              value={report.remarks}
              onChange={(e) => setReport({ ...report, remarks: e.target.value })}
              placeholder="Any additional notes..."
              data-testid="report-remarks"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={handleSkip}>
            Skip & Logout
          </Button>
          <Button onClick={handleSubmit} disabled={saving} data-testid="submit-report-btn">
            {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Submit & Logout
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default DailyReportDialog;
